import {
  ITogglesIsDisabledCond,
  OfferingsSwitchList,
} from 'legacy/lut/components/widgets/public/partner360/offeringsSwithList/OfferingsSwitchList'
import { IOfferingChange } from 'legacy/lut/components/widgets/public/partner360/offeringsSwithList/OfferingSwitcher'
import { WidgetHeader } from 'legacy/lut/components/widgets/public/partner360/widgetHeader/WidgetHeader'
import { selectCommitmentStatuses } from 'legacy/lut/features/partner360/commitment'
import {
  changeOutOfScope,
  IProgramType,
  selectPartnerAccess,
  selectPartnerDataEntryId,
} from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { toggleOffering } from 'partner360/helpers/offerings/toggleOffering'

import { toggleScopeValue } from './api/toggleScopeValue'

import styles from './ProgramTypesEditorModal.module.scss'

interface IProgramTypesEditorModalProps {
  partnerId: string
  fYear: number
  programTypes: IProgramType[]
  shouldShowModal: boolean

  onClose: () => void
}

export const ProgramTypesEditorModal: FC<IProgramTypesEditorModalProps> = ({
  partnerId,
  fYear,
  programTypes,
  shouldShowModal,
  onClose,
}) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const commitmentStatuses = useSelector(selectCommitmentStatuses)
  const partnerDataEntryId = useSelector(selectPartnerDataEntryId)
  const ownerId = useSelector(selectAppOwnerId)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const scopeControllerIsDisabled = useMemo(() => {
    return isUpdating
  }, [isUpdating])

  const dispatch = useAppDispatch()

  const widgetHeader = useCallback(() => {
    const content = () => (
      <>
        {isUpdating && (
          <Loader
            className={styles.specialisationEditorLoader}
            dimension={{ width: 20, height: 20 }}
          />
        )}
      </>
    )

    return (
      <div className={styles.specialisationEditorHeader}>
        <WidgetHeader content={content}>jbp-partner-program-types-header</WidgetHeader>
      </div>
    )
  }, [isUpdating])

  const handleToggleOffering = useCallback(
    async ({ offering, flag }: IOfferingChange) => {
      setIsUpdating(true)
      await toggleOffering({
        fYear,
        offering,
        flag,
        partnerId,
        dispatch,
        commitmentStatuses,
        isDistributor,
        ownerId,
      })
      setIsUpdating(false)
    },
    [commitmentStatuses, fYear, isDistributor, ownerId, partnerId, dispatch]
  )

  const handleToggleScope = useCallback(
    async (outOfScope: number) => {
      setIsUpdating(true)
      await toggleScopeValue({ entryId: partnerDataEntryId, outOfScope })
      dispatch(changeOutOfScope(Boolean(outOfScope)))
      setIsUpdating(false)
      onClose()
    },
    [dispatch, onClose, partnerDataEntryId]
  )

  const getIsDisabledCondition = useCallback(
    ({ enabledOfferings, offering }: ITogglesIsDisabledCond) => {
      const isLastEnabledOffering = enabledOfferings.length === 1 && offering.enabled
      return isUpdating || isLastEnabledOffering
    },
    [isUpdating]
  )

  return (
    <ModalDialog
      header={widgetHeader}
      show={shouldShowModal}
      onClose={onClose}
      size="xs"
      contentProps={{
        programTypes,
        onToggleOffering: handleToggleOffering,
        togglesIsDisabledCond: getIsDisabledCondition,
        onToggleScopeValue: handleToggleScope,
        scopeControllerIsDisabled,
        needShowWarning: isDistributor,
      }}
      content={OfferingsSwitchList}
    />
  )
}
